import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Divider,
  Grid,
  LinearProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { useSnackbar } from 'notistack';

const StudentIdCard = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('student-portal/profile');
      if (response.data.success) {
        setStudentData(response.data.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch student data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const downloadIdCard = async () => {
    try {
      const response = await axios.get('student-portal/id-card', {
        responseType: 'blob', // Important for PDF download
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename
      const filename = `${studentData?.first_name || 'Student'}_${studentData?.last_name || 'ID'}_Card.pdf`;
      link.setAttribute('download', filename);
      
      // Append to body and click
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('ID Card downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar('Failed to download ID card', { variant: 'error' });
    }
  };

  const printIdCard = () => {
    // Wait a moment to ensure the hidden element is rendered
    setTimeout(() => {
      const idCardElement = document.getElementById('id-card-content');
      
      if (!idCardElement) {
        enqueueSnackbar('Print element not found. Please try again.', { variant: 'error' });
        return;
      }
      
      // Create a new window with only the ID card content
      const printWindow = window.open('', '_blank', 'width=400,height=300');
      
      if (!printWindow) {
        enqueueSnackbar('Unable to open print window. Please check popup settings.', { variant: 'error' });
        return;
      }
      
      try {
        printWindow.document.write(`
          <html>
            <head>
              <title>Student ID Card - ${studentData?.first_name || ''} ${studentData?.last_name || ''}</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  font-family: Arial, sans-serif;
                  background: white;
                }
                .id-card {
                  width: 350px;
                  height: 220px;
                  margin: 0 auto;
                  border: 2px solid #3498db;
                  border-radius: 10px;
                  padding: 20px;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                }
                .header {
                  text-align: center;
                  margin-bottom: 15px;
                }
                .school-name {
                  font-size: 18px;
                  font-weight: bold;
                  color: #2c3e50;
                  margin: 0;
                }
                .school-subtitle {
                  font-size: 10px;
                  color: #7f8c8d;
                  margin: 5px 0;
                }
                .content {
                  display: flex;
                  align-items: flex-start;
                  gap: 15px;
                }
                .photo {
                  width: 60px;
                  height: 60px;
                  border-radius: 50%;
                  background: #ecf0f1;
                  border: 2px solid #bdc3c7;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 16px;
                  font-weight: bold;
                  color: #2c3e50;
                  flex-shrink: 0;
                }
                .info {
                  flex: 1;
                }
                .name {
                  font-size: 16px;
                  font-weight: bold;
                  color: #2c3e50;
                  margin: 0 0 8px 0;
                }
                .details {
                  font-size: 11px;
                  color: #34495e;
                  line-height: 1.4;
                }
                .details div {
                  margin-bottom: 2px;
                }
                .academic-info {
                  margin-top: 15px;
                  font-size: 9px;
                  color: #7f8c8d;
                  display: flex;
                  justify-content: space-between;
                }
                .footer {
                  margin-top: 10px;
                  font-size: 8px;
                  color: #95a5a6;
                  display: flex;
                  justify-content: space-between;
                }
                @media print {
                  body { margin: 0; padding: 10px; }
                  .id-card { box-shadow: none; }
                }
              </style>
            </head>
            <body>
              ${idCardElement.innerHTML}
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Wait for content to load before printing
        printWindow.onload = () => {
          printWindow.print();
          printWindow.close();
        };
        
        // Fallback timeout
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.print();
            printWindow.close();
          }
        }, 1000);
        
        enqueueSnackbar('Print dialog opened', { variant: 'success' });
        
      } catch (error) {
        console.error('Print error:', error);
        enqueueSnackbar('Failed to open print dialog', { variant: 'error' });
        printWindow.close();
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          Student ID Card
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!studentData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          Student ID Card
        </Typography>
        <Alert severity="error">
          Failed to load student data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Student ID Card
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={printIdCard}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadIdCard}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {/* ID Card Preview */}
        <Grid item xs={12} md={8} lg={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card 
              sx={{ 
                maxWidth: 400,
                mx: 'auto',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
              }}
            >
              {/* Header */}
              <Box sx={{ 
                p: 2, 
                textAlign: 'center',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)'
              }}>
                <SchoolIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  EDUCATION MANAGEMENT SYSTEM
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Student Identity Card
                </Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Student Photo and Basic Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mr: 2,
                      border: '3px solid white',
                      fontSize: '2rem'
                    }}
                    src={studentData.profile_picture ? 
                      `http://localhost:3000/uploads/profiles/${studentData.profile_picture}` : 
                      undefined
                    }
                  >
                    {studentData.first_name?.charAt(0)}{studentData.last_name?.charAt(0)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {studentData.first_name} {studentData.last_name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Student ID: {studentData.student_code || studentData.student_id}
                    </Typography>
                    <Chip 
                      label={`Class ${studentData.class_name || 'N/A'}`}
                      size="small"
                      sx={{ 
                        mt: 0.5,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white'
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 2 }} />

                {/* Student Details */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Roll Number
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {studentData.roll_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Section
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {studentData.section || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Date of Birth
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {studentData.date_of_birth ? 
                        new Date(studentData.date_of_birth).toLocaleDateString() : 
                        'N/A'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Blood Group
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {studentData.blood_group || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Emergency Contact
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {studentData.emergency_contact_phone || studentData.phone || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', my: 2 }} />

                {/* Academic Year and Validity */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Academic Year
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {new Date().getFullYear()}-{new Date().getFullYear() + 1}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 1 }}>
                    Valid until: {new Date(new Date().getFullYear() + 1, 5, 30).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>

              {/* Footer */}
              <Box sx={{ 
                p: 1, 
                textAlign: 'center',
                background: 'rgba(0,0,0,0.2)',
                fontSize: '0.75rem'
              }}>
                <Typography variant="caption">
                  This card is property of the institution. If found, please return to administration.
                </Typography>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Hidden Printable ID Card */}
        <div id="id-card-content" style={{ display: 'none' }}>
          <div className="id-card">
            <div className="header">
              <h2 className="school-name">EMIS SCHOOL</h2>
              <p className="school-subtitle">Education Management Information System</p>
            </div>
            <div className="content">
              <div className="photo">
                {studentData?.first_name?.charAt(0)}{studentData?.last_name?.charAt(0)}
              </div>
              <div className="info">
                <h3 className="name">{studentData?.first_name} {studentData?.last_name}</h3>
                <div className="details">
                  <div>Student ID: {studentData?.student_code || studentData?.student_id}</div>
                  <div>Class: {studentData?.class_name || 'N/A'} {studentData?.section_name || ''}</div>
                  <div>Roll No: {studentData?.roll_number || 'N/A'}</div>
                  <div>DOB: {studentData?.date_of_birth ? new Date(studentData.date_of_birth).toLocaleDateString() : 'N/A'}</div>
                </div>
              </div>
            </div>
            <div className="academic-info">
              <span>GPA: {parseFloat(studentData?.gpa || 0).toFixed(2)}</span>
              <span>Attendance: {parseFloat(studentData?.attendance_percentage || 0).toFixed(1)}%</span>
              <span>Session: {new Date().getFullYear()}-{new Date().getFullYear() + 1}</span>
            </div>
            <div className="footer">
              <span>Issued: {new Date().toLocaleDateString()}</span>
              <span>Valid for Academic Year</span>
            </div>
          </div>
        </div>

        {/* Information Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <BadgeIcon sx={{ mr: 1 }} />
                ID Card Information
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Your student ID card contains all essential information for identification within the institution.
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Important Notes:
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  • Always carry your ID card while on campus
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  • Report lost or damaged cards immediately
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  • ID card is required for library access, exams, and events
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  • Replacement fee applies for lost cards
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Card Features:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">Personal Information</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SchoolIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">Academic Details</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">Validity Period</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadIdCard}
                  >
                    Download Digital Copy
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={printIdCard}
                  >
                    Print ID Card
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentIdCard;
